import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge, type BadgeProps } from '../ui/badge';
import {
  ObservabilityHealth,
  ObservabilitySignal,
  ObservabilitySource,
} from '@/lib/api/observability';
import { relativeDate } from '@/lib/utils';
import { appRoutes } from '@/router';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, CircleAlert } from 'lucide-react';

export {
  mergeObservabilitySources,
  observabilityDataUnavailable,
  observabilitySourceCurrent,
  observabilitySourceUnavailable,
} from './source-status';

const healthVariant: Record<
  ObservabilityHealth,
  NonNullable<BadgeProps['variant']>
> = {
  healthy: 'successful',
  degraded: 'inProgress',
  critical: 'failed',
  unknown: 'outline',
};

export function HealthBadge(props: { health: ObservabilityHealth }) {
  return (
    <Badge variant={healthVariant[props.health]} className="capitalize">
      {props.health}
    </Badge>
  );
}

export function ObservabilitySignals(props: {
  signals: ObservabilitySignal[];
  tenantId?: string;
}) {
  if (props.signals.length === 0) {
    return null;
  }

  return (
    <Alert
      variant={
        props.signals.some((signal) => signal.severity === 'critical')
          ? 'destructive'
          : 'warn'
      }
    >
      <CircleAlert />
      <AlertTitle>Operational signals</AlertTitle>
      <AlertDescription className="space-y-2">
        {props.signals.map((signal) => (
          <div key={signal.id}>
            <span className="font-medium">{signal.title}</span>
            <span className="text-muted-foreground"> · {signal.summary}</span>
            {props.tenantId && signal.runId && (
              <Link
                className="ml-2 font-medium underline underline-offset-2"
                to={appRoutes.tenantRunRoute.to}
                params={{ tenant: props.tenantId, run: signal.runId }}
              >
                View run
              </Link>
            )}
          </div>
        ))}
      </AlertDescription>
    </Alert>
  );
}

export function ObservabilitySourceAlert(props: {
  partial: boolean;
  sources: ObservabilitySource[];
}) {
  const affectedSources = props.sources.filter(
    (source) => source.status !== 'ok',
  );

  if (!props.partial && affectedSources.length === 0) {
    return null;
  }

  const stale =
    affectedSources.length > 0 &&
    affectedSources.every((source) => source.status === 'stale');
  const unavailable = affectedSources.some((source) =>
    ['error', 'unavailable', 'not_configured', 'forbidden'].includes(
      source.status,
    ),
  );

  return (
    <Alert variant={unavailable ? 'destructive' : 'warn'}>
      {unavailable ? <CircleAlert /> : <AlertTriangle />}
      <AlertTitle>
        {stale
          ? 'Telemetry is stale'
          : unavailable
            ? 'Telemetry unavailable'
            : 'Partial telemetry'}
      </AlertTitle>
      <AlertDescription>
        {affectedSources.length > 0 ? (
          <p>
            {affectedSources
              .map(
                (source) =>
                  `${source.id}: ${source.status.replace('_', ' ')}${source.observedAt ? ` (${relativeDate(source.observedAt)})` : ''}`,
              )
              .join(' · ')}
          </p>
        ) : (
          <p>Some telemetry was omitted from this response.</p>
        )}
        {affectedSources.some((source) => source.message) && (
          <p className="mt-1 text-xs opacity-80">
            {affectedSources
              .filter((source) => source.message)
              .map((source) => source.message)
              .join(' · ')}
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}
